<script lang="ts">
	import { goto } from '$app/navigation';
	import { marketsConfig } from '$lib/config/markets';
	import { getDisplayName, getTokenLogo } from '$lib/perps';
	import type { Market, MarketIdentifiers } from '$lib/types/market';
	import {
		addDollarSign,
		filterMarketsBasedOnSearch,
		getNumBackInUnits,
		nFormatter
	} from '$lib/util/util';
	import { smartWalletCreationPending, walletConnected } from '$lib/wallet/store';
	import { quadIn, quadInOut, quadOut } from 'svelte/easing';
	import { fade, fly, scale } from 'svelte/transition';
	import { markets } from '../store';
	import { isTPSLActive, userMarginList } from '../userInteract/store';
	import MarketSearch from './MarketSearch.svelte';
	import AllOrFavouriteSelector from './AllOrFavouriteSelector.svelte';
	import { favouriteMarkets } from './store';
	import LazyLoadWrapper from '$lib/util/LazyLoadWrapper.svelte';

	export let showFavouritesOnly: boolean = false;

	export let showMarketSelector = false;
	export let closeMenu = () => {
		showMarketSelector = false;
	};

	function openMarket(marketId: MarketIdentifiers) {
		goto(`/perps?market=${marketId}`);
		showMarketSelector = false;
		$isTPSLActive = false;
	}

	function getUserMargin(marketId: MarketIdentifiers) {
		// from getUserMargin return the depositedMargin for the markerId
		let depositedMargin = $userMarginList.find((margin) => margin?.marketIdentifier == marketId)
			?.depositedMargin as string;
		if (depositedMargin) return getNumBackInUnits(depositedMargin, 18, 2);
		else return '';
	}

	function pushMarketToFavourites(id: MarketIdentifiers) {
		let tempMarkets = $favouriteMarkets;
		tempMarkets.push(id);
		favouriteMarkets.set(tempMarkets);
	}

	function removeMarketFromFavourites(id: MarketIdentifiers) {
		let tempMarkets = $favouriteMarkets;
		tempMarkets.splice(tempMarkets.indexOf(id), 1);
		favouriteMarkets.set(tempMarkets);
	}

	export function filterMarketsBasedOnFavourites(
		markets: Market[],
		favouriteMarkets: MarketIdentifiers[]
	) {
		return markets?.filter((market) => {
			const isFavourite = favouriteMarkets.find((m) => {
				return market.marketIdentifier === m;
			});

			if (isFavourite) return true;
			return false;
		});
	}

	export function filterMarketsToShow(
		markets: Market[],
		searchText: string,
		showFavouritesOnly: boolean,
		favouriteMarkets: MarketIdentifiers[]
	) {
		if (showFavouritesOnly)
			return filterMarketsBasedOnSearch(
				filterMarketsBasedOnFavourites(markets, favouriteMarkets),
				searchText
			);
		return filterMarketsBasedOnSearch(markets, searchText);
	}

	let searchText = '';
	$: filteredMarkets = filterMarketsToShow(
		$markets,
		searchText,
		showFavouritesOnly,
		$favouriteMarkets
	);
</script>

{#if showMarketSelector && $markets}
	<div
		class="modal relative w-full flex justify-end z-10"
		in:fade={{
			duration: 250,
			easing: quadOut
		}}
	>
		<div
			on:click={closeMenu}
			on:keydown={closeMenu}
			class="w-full fixed h-screen top-0 left-0 bg-fx-black bg-opacity-75 sm:bg-opacity-0 no-scrollbar"
			role="presentation"
		/>
		<div
			class="w-full sm:w-fit fixed sm:absolute bg-fx-gradient-8 z-50 transition-right duration-500 left-0 sm:mx-4 mt-0 p-2 gap-1 rounded-xl squircle-xl bottom-0 sm:bottom-auto h-[80vh] overflow-y-auto sm:min-w-[335px] flex flex-col"
			transition:scale={{ duration: 10, delay: 0, opacity: 0.9, start: 0.97, easing: quadInOut }}
		>
			<MarketSearch bind:searchText />
			<AllOrFavouriteSelector
				numberOfMarkets={$markets.length}
				numberOfFavouriteMarkets={$favouriteMarkets.length}
				bind:showFavouritesOnly
			/>

			{#if filteredMarkets.length > 0}
				<div class="overflow-y-auto no-scrollbar">
					{#each filteredMarkets as market, i}
						<LazyLoadWrapper defaultClass="h-[52px]">
							<div
								class="flex gap-1 items-center"
								in:fly={{
									duration: 10 * Math.sqrt(i),
									x: 0,
									y: 30,
									opacity: 0.1,
									easing: quadIn
								}}
							>
								{#if $favouriteMarkets.includes(market.marketIdentifier)}
									<div
										class="cursor-pointer py-2"
										on:click={() => removeMarketFromFavourites(market.marketIdentifier)}
										on:keydown={() => removeMarketFromFavourites(market.marketIdentifier)}
										role="presentation"
									>
										<img loading="lazy" src={'markets/favourite.png'} alt="favourite" class="w-4" />
									</div>
								{:else}
									<div
										class="cursor-pointer py-2"
										on:click={() => pushMarketToFavourites(market.marketIdentifier)}
										on:keydown={() => pushMarketToFavourites(market.marketIdentifier)}
										role="presentation"
									>
										<img
											loading="lazy"
											src={'markets/nonfavourite.png'}
											alt="favourite"
											class="w-4"
										/>
									</div>
								{/if}
								<div
									class="flex gap-3 px-2.5 py-2 rounded-xl squircle-xl cursor-pointer text-fx-grey-300 hover:text-fx-grey-200 hover:bg-fx-grey-700 transform duration-200 w-full ease-out"
									on:click={() => openMarket(market.marketIdentifier)}
									on:keydown={() => openMarket(market.marketIdentifier)}
									role="presentation"
								>
									<img
										loading="lazy"
										src={getTokenLogo(market.marketIdentifier)}
										alt="logo"
										class="w-8 h-8 rounded-full"
									/>
									<div class="grid w-full">
										<div class="flex w-full justify-between items-center">
											<div class="flex-col">
												<div class="text-sm rounded-lg squircle-lg font-medium pr-20">
													{getDisplayName(market.marketIdentifier)}
												</div>
												<div class="text-fx-grey-300 font-medium text-xs flex gap-1">
													{#if $walletConnected}
														<span class="text-fx-grey-400"> Margin </span>
														{#if getUserMargin(market.marketIdentifier)}
															{getUserMargin(market.marketIdentifier)}
														{:else if $smartWalletCreationPending}
															$0
														{:else}
															<div
																class="animate-pulse bg-fx-grey-800 h-4 col-span-2 rounded-lg squircle-lg w-6 mx-auto"
															/>
														{/if}
													{:else}
														<span class="text-fx-grey-400">24h Volume: </span>
														${nFormatter(getNumBackInUnits(market.volume24h ?? 0, 18, 2), 0)}
													{/if}
												</div>
											</div>

											<div>
												<div class="text-fx-white font-medium text-xs text-right">
													{addDollarSign(
														getNumBackInUnits(
															market.currentMarketPrice,
															18,
															marketsConfig[market.asset].showDecimals
														),
														marketsConfig[market.asset].showDecimals
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</LazyLoadWrapper>
					{/each}
				</div>
			{:else}
				<div class="text-fx-grey-300 text-sm">No results</div>
			{/if}
		</div>
	</div>
{/if}
